/**
 * Copyright © 2026 Delicious AI, LLC
 */

import type { UltraLabelingExplorerQuery } from '@dai/graphql'
import {
  UltraCompanyChoices,
  UltraLabelingExplorerBoxStatus,
} from '@dai/graphql'

export type LabelingExplorerItem =
  UltraLabelingExplorerQuery['labelingExplorer']['items'][number]
export type LabelingExplorerImage = LabelingExplorerItem['image']
export type LabelingExplorerBox = NonNullable<LabelingExplorerItem['box']>
export type LabelingExplorerPredictedUPC =
  LabelingExplorerBox['predictedUpcs'][number]

export type ExplorerProduct = {
  upcA: string
  preferredName?: string | null
  name?: string | null
  packagingName?: string | null
  commonName?: { name?: string | null } | null
  brand?: { name?: string | null } | null
  category?: { name?: string | null } | null
}

export type ExplorerBoxTone =
  | 'unidentified'
  | 'close'
  | 'unverified'
  | 'verified'

export const getExplorerBoxTone = (
  box: Pick<
    LabelingExplorerBox,
    'isUnidentified' | 'classifierScoreMargin' | 'isVerified'
  >,
  closeConfidenceThreshold: number | null,
): ExplorerBoxTone => {
  if (box.isUnidentified) {
    return 'unidentified'
  }

  if (
    closeConfidenceThreshold !== null &&
    box.classifierScoreMargin !== null &&
    box.classifierScoreMargin <= closeConfidenceThreshold
  ) {
    return 'close'
  }

  return box.isVerified ? 'verified' : 'unverified'
}

export const getExplorerBoxColor = (
  tone: ExplorerBoxTone,
): 'default' | 'warning' | 'success' | 'info' => {
  if (tone === 'unidentified') {
    return 'default'
  }
  if (tone === 'close') {
    return 'warning'
  }
  if (tone === 'unverified') {
    return 'info'
  }
  return 'success'
}

export const getBoxDisplayUpc = (box: LabelingExplorerBox) =>
  box.verifiedUpc ?? box.currentUpc ?? box.predictedUpcs[0]?.upc ?? null

export const normalizeUpcKey = (upc: string | null | undefined) =>
  upc?.trim() ?? null

export const collectVisibleUpcs = (items: LabelingExplorerItem[]) =>
  Array.from(
    new Set(
      items
        .flatMap(item => [
          ...(item.box ? [item.box] : []),
          ...(item.image.boundingBoxes ?? []),
        ])
        .flatMap(box => [
          box.verifiedUpc,
          box.currentUpc,
          ...box.predictedUpcs.map(prediction => prediction.upc),
        ])
        .filter((upc): upc is string => Boolean(upc)),
    ),
  )

export const getProductLabel = (
  upc: string | null,
  productsByUpc: Record<string, ExplorerProduct>,
) => {
  const upcKey = normalizeUpcKey(upc)
  if (!upcKey) {
    return 'Unidentified'
  }
  const product = productsByUpc[upcKey]
  if (!product) {
    return upcKey
  }
  return [
    product.commonName?.name || product.preferredName || product.name || upcKey,
    product.packagingName,
    product.brand?.name,
  ]
    .filter(Boolean)
    .join(' · ')
}

export const hasProductDetails = (
  upc: string | null,
  productsByUpc: Record<string, ExplorerProduct>,
) => {
  const upcKey = normalizeUpcKey(upc)
  return Boolean(upcKey && productsByUpc[upcKey])
}

export const toneStyles = {
  unidentified: {
    border: '#9e9e9e',
    background: 'rgba(158, 158, 158, 0.18)',
  },
  close: {
    border: '#f9a825',
    background: 'rgba(249, 168, 37, 0.18)',
  },
  unverified: {
    border: '#1976d2',
    background: 'rgba(25, 118, 210, 0.16)',
  },
  verified: {
    border: '#2e7d32',
    background: 'rgba(46, 125, 50, 0.16)',
  },
}

export const formatScore = (score: number | null) =>
  score === null ? 'n/a' : score.toFixed(3)

export const formatCreatedDate = (created: string) =>
  new Date(created).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

export const formatToneLabel = (tone: ExplorerBoxTone) => {
  if (tone === 'close') {
    return 'Close top-2'
  }
  return tone[0].toUpperCase() + tone.slice(1)
}

export const formatUltraCompanyChoices = (
  sourceCustomer: UltraCompanyChoices | null,
) =>
  sourceCustomer
    ? sourceCustomer
        .toLowerCase()
        .replace(
          /(^|_)([a-z])/g,
          (_, separator, letter: string) =>
            `${separator ? ' ' : ''}${letter.toUpperCase()}`,
        )
    : 'Unknown'

export const formatStatusLabel = (
  status: UltraLabelingExplorerBoxStatus,
): string => {
  switch (status) {
    case UltraLabelingExplorerBoxStatus.UNIDENTIFIED:
      return 'Unidentified'
    case UltraLabelingExplorerBoxStatus.CLOSE_TOP_2:
      return 'Close top-2'
    case UltraLabelingExplorerBoxStatus.VERIFIED:
      return 'Verified'
    case UltraLabelingExplorerBoxStatus.UNVERIFIED:
      return 'Unverified'
  }
}

export const getImageToneCounts = (
  image: LabelingExplorerImage,
  closeConfidenceThreshold: number | null,
) =>
  (image.boundingBoxes ?? []).reduce<Record<ExplorerBoxTone, number>>(
    (counts, box) => {
      counts[getExplorerBoxTone(box, closeConfidenceThreshold)] += 1
      return counts
    },
    {
      unidentified: 0,
      close: 0,
      unverified: 0,
      verified: 0,
    },
  )
